import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Match,
  WarehouseMatch,
  WarehouseMatchList,
} from '@views/WarehouseDetails/WarehouseDetails';
import { DocumentTextIcon, LinkIcon } from '@heroicons/react/24/outline';
import { isSameBusiness, toDisplayDate } from '@utils';
import { useAuth } from '../../context';
import { useAuthHttp } from '../../hooks';
import { CONTRACTED_MATCH_LIST } from '../../constants';
import { businessProfileAtom } from '../../store/jotai';
import { UserRoleService } from '../../services';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { PricingScheduleModal } from '../../tailwindComponents/PricingScheduleModal';

const acceptedOnOf = (match: Match) =>
  match.acceptedAgreement
    ? toDisplayDate(match.acceptedAgreement.acceptedAt)
    : 'N/A';

const acceptingCompanyOf = ({ match, warehouse }: WarehouseMatch) =>
  isSameBusiness(
    match.acceptedAgreement?.acceptedForBusinessId,
    match.userBusinessId
  )
    ? match.userBusinessName
    : match.lspBusinessName ?? warehouse.userBusinessName;

const acceptedByOf = (contractedMatch: WarehouseMatch) => {
  const { acceptedAgreement } = contractedMatch.match;
  if (!acceptedAgreement) return 'N/A';

  const acceptingCompany = acceptingCompanyOf(contractedMatch);
  return acceptedAgreement.acceptedByName
    ? `${acceptedAgreement.acceptedByName} for ${acceptingCompany}`
    : acceptingCompany;
};

export function LegalDocumentsHub() {
  const { user } = useAuth();
  const { isWarehouseManager } = UserRoleService();
  const authHttp = useAuthHttp();
  const [contractedMatches, setContractedMatches] = useState<WarehouseMatch[]>(
    []
  );
  const [viewedMatchId, setViewedMatchId] = useState('');
  const [isPricingScheduleOpen, setIsPricingScheduleOpen] = useState(false);
  const businessProfile = useAtomValue(businessProfileAtom);

  useEffect(() => {
    const fetchContractedMatches = async () => {
      try {
        const { data } = await authHttp.post<WarehouseMatchList>(
          CONTRACTED_MATCH_LIST,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (data) {
          setContractedMatches(data.matches);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    };
    fetchContractedMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAgreedToWarehouseTandCs = Boolean(
    businessProfile?.warehouseTandCAgreement?.hasAgreed
  );

  const showWarehouseTandCAgreement = isWarehouseManager(user)
    ? hasAgreedToWarehouseTandCs
    : contractedMatches.length >= 1;

  const contractAcceptedByThisBusiness = contractedMatches.find(({ match }) =>
    isSameBusiness(
      match.acceptedAgreement?.acceptedForBusinessId,
      user?.businessGroupId
    )
  );

  const agreements = contractedMatches.map((contractedMatch) => {
    const { match, warehouse } = contractedMatch;
    const isViewedByTheShipper = isSameBusiness(
      match.userBusinessId,
      user?.businessGroupId
    );

    return {
      matchId: match.id,
      warehouseName: warehouse.name,
      agreedWith: isViewedByTheShipper
        ? match.lspBusinessName ?? warehouse.userBusinessName
        : match.userBusinessName,
      acceptedOn: acceptedOnOf(match),
      amendedOn: match.amendedAt ? toDisplayDate(match.amendedAt) : null,
      acceptedBy: acceptedByOf(contractedMatch),
      canBeViewed: Boolean(match.acceptedAgreement),
    };
  });

  const viewAgreement = (matchId: string) => {
    setViewedMatchId(matchId);
    setIsPricingScheduleOpen(true);
  };

  const pages = [
    { name: 'Legal Documents', href: '/legal-documents', current: true },
  ];

  return (
    <>
      <PageHeader
        header='Legal Documents Hub'
        subHeader='View and manage your legal agreements and contracts'
        pages={pages}
      />
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
        {user?.roles.includes('BusinessUserManager') && (
          <>
            {/* Platform Agreements Card */}
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
                <div className='flex items-center gap-3'>
                  <LinkIcon className='h-5 w-5 text-gray-400' />
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Platform Agreements
                  </h2>
                </div>
              </div>
              <div className='p-6 space-y-4'>
                <div className='flex items-center gap-2'>
                  <a
                    href='https://www.flox.is/platform-terms'
                    target='_blank'
                    rel='noreferrer'
                    className='text-sm text-blue-600 hover:text-blue-800 hover:underline'
                  >
                    Platform Terms and Conditions
                  </a>
                  <span className='text-sm text-gray-500'>
                    - accepted by {user.name}
                  </span>
                </div>
                {showWarehouseTandCAgreement && (
                  <div className='flex items-center gap-2'>
                    <a
                      href='https://www.flox.is/standard-conditions-of-warehousing-services'
                      className='text-sm text-blue-600 hover:text-blue-800 hover:underline'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Standard Conditions of Warehousing Services
                    </a>
                    {isWarehouseManager(user) ? (
                      <span className='text-sm text-gray-500'>
                        - accepted{' '}
                        {(() => {
                          const date = new Date(
                            businessProfile?.warehouseTandCAgreement?.agreementDate
                          );
                          return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}/${date.getFullYear()}`;
                        })()}{' '}
                        by{' '}
                        {businessProfile?.warehouseTandCAgreement?.agreedByName}
                      </span>
                    ) : (
                      contractAcceptedByThisBusiness?.match.acceptedAgreement
                        ?.acceptedByName && (
                        <span className='text-sm text-gray-500'>
                          - accepted by{' '}
                          {acceptedByOf(contractAcceptedByThisBusiness)}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Price Schedules Card */}
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
                <div className='flex items-center gap-3'>
                  <DocumentTextIcon className='h-5 w-5 text-gray-400' />
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Warehousing Service Price Schedules
                  </h2>
                </div>
              </div>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th
                        scope='col'
                        className='py-3.5 pl-6 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        #
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Warehouse
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Agreed with
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Accepted on
                      </th>
                      <th
                        scope='col'
                        className='px-3 py-3.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                      >
                        Accepted by
                      </th>
                      <th scope='col' className='py-3.5 pl-3 pr-6'>
                        <span className='sr-only'>Pricing Schedule</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white'>
                    {agreements.map((agreement, i) => (
                      <tr
                        key={agreement.matchId}
                        className='hover:bg-gray-50 transition-colors'
                      >
                        <td className='whitespace-nowrap py-4 pl-6 pr-3 text-sm font-medium text-gray-900'>
                          {i + 1}
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-900'>
                          {agreement.warehouseName}
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-900'>
                          {agreement.agreedWith}
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                          {agreement.acceptedOn}
                          {agreement.amendedOn && (
                            <span className='block text-xs text-gray-400'>
                              Amended by FLOX on {agreement.amendedOn}
                            </span>
                          )}
                        </td>
                        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>
                          {agreement.acceptedBy}
                        </td>
                        <td className='whitespace-nowrap py-4 pl-3 pr-6 text-right text-sm'>
                          {agreement.canBeViewed && (
                            <Button
                              label='View'
                              intent='secondary'
                              size='xs'
                              className='h-auto'
                              onClick={() => viewAgreement(agreement.matchId)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                    {agreements.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className='py-8 text-center text-sm text-gray-500'
                        >
                          No service agreements found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        <PricingScheduleModal
          matchId={viewedMatchId}
          isOpen={isPricingScheduleOpen}
          setIsOpen={setIsPricingScheduleOpen}
          mode='view'
        />
      </main>
    </>
  );
}
